/**
 * Expense Breakdown Chart Component
 * Pie chart showing expense category distribution
 */

import React from 'react';
import {
  ChartContainer,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  CustomTooltip,
  formatCurrency,
  CHART_COLORS
} from './FinancialChart';
import { ExpenseCategory } from '../../../types/financialForecasting';

interface ExpenseBreakdownChartProps {
  expenses: ExpenseCategory[];
}

const COLORS = [
  CHART_COLORS.primary,
  CHART_COLORS.secondary,
  CHART_COLORS.tertiary,
  CHART_COLORS.warning,
  '#EC4899', // pink-500
  '#8B5CF6', // purple-500
  '#6366F1', // indigo-500
  '#14B8A6', // teal-500
];

export const ExpenseBreakdownChart: React.FC<ExpenseBreakdownChartProps> = ({
  expenses
}) => {
  // Calculate total expenses per category (sum across all periods)
  const categoryTotals = expenses.map(expense => {
    const total = expense.projections.reduce((sum, val) => sum + val, 0);
    return {
      name: expense.name,
      value: total,
      category: expense.category
    };
  });

  // Sort by value descending
  const sortedData = categoryTotals.sort((a, b) => b.value - a.value);

  return (
    <ChartContainer
      title="Expense Breakdown"
      subtitle="Annual expense distribution by category"
    >
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={sortedData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={({ name, percent }) => 
              percent > 0.05 ? `${name} ${(percent * 100).toFixed(0)}%` : ''
            }
            outerRadius={100}
            fill="#8884d8"
            dataKey="value"
          >
            {sortedData.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={COLORS[index % COLORS.length]}
              />
            ))}
          </Pie>
          <Tooltip
            content={<CustomTooltip formatter={formatCurrency} />}
          />
          <Legend
            verticalAlign="bottom"
            height={36}
            iconType="circle"
          />
        </PieChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};

export default ExpenseBreakdownChart;
